/**
 * Response from /api/customer-documents/requests endpoint
 * Always returns an array, even when filtered by order_id
 */
export type CustomerDocumentRequestResponse = {
  id: number | string; // Can be string for large IDs that exceed Number.MAX_SAFE_INTEGER
  external_references: ExternalReferenceResponse[] | null;
  document_request_templates: DocumentRequestTemplateResponse[];
  customer_document_approvals: CustomerDocumentApprovalResponse[] | null;
  /**
   * Trade-in only: what this flow carries on a request that customer-api does
   * not read. `customer_document_requests` is shared with the purchase flow, so
   * these live together rather than as a column each.
   *
   * `link_token` is in here upstream and is the seller's whole credential — the
   * BFF strips it before this type is ever populated, which is why it is absent
   * below rather than optional.
   */
  context?: {
    /** What the form shows without any acquisition access. */
    display_snapshot?: TradeInDisplaySnapshot | null;
    /** The two ownership answers, once saved. Absent until the seller answers. */
    seller_answers?: TradeInSellerAnswers | null;
    /** Acquisition facts captured at issuance, used to pre-fill the questions. */
    condition_facts?: TradeInConditionFacts | null;
  } | null;
};

/** Boomerang seller types, verbatim — the values the ownership question maps onto. */
export enum TradeInSellerType {
  INDIVIDUAL = 'st_individual',
  INDIVIDUAL_JOINT = 'st_individual_joint',
  INDIVIDUAL_THIRD_PARTY = 'st_individual_thirdparty',
  COMPANY_SMALL = 'st_company_small',
  CORPORATE_GOVERNMENT = 'st_corporate_government',
  REGISTERED_DEALER = 'st_registered_dealer',
}

export const TRADE_IN_SELLER_TYPES: TradeInSellerType[] = Object.values(TradeInSellerType);

export type TradeInDisplaySnapshot = {
  seller_first_name: string;
  vehicle_description: string;
  rego?: string | null;
};

export type TradeInConditionFacts = {
  finance_owing?: boolean | null;
};

/** Body of the answers endpoint, and the shape stored back on the request. */
export type TradeInSellerAnswers = {
  seller_type: TradeInSellerType;
  finance_owing: boolean;
};

export type ExternalReferenceResponse = {
  id: number | string;
  external_id: string;
  external_type: string;
  source: string;
};

export type DocumentRequestTemplateResponse = {
  id: number | string;
  description: string;
  name: string;
  /** Use for CMS/Contentful lookup when present */
  content_key?: string;
  /** Stable identifier, e.g. `trade_in_documents`. */
  code?: string;
};

export type CustomerDocumentApprovalResponse = {
  id: number | string;
  document_templates: DocumentTemplateResponse[];
  customer_documents: CustomerDocumentResponse[];
  status: string; // API returns lowercase string: "pending", "submitted", "approved", etc.
  /**
   * Reviewer's reason, set when CX rejects a document. Only present on rejected
   * approvals, and only since the BFF stopped stripping it from every response.
   */
  notes?: string | null;
  /**
   * Sets this document belongs to, where the section needs only some of its
   * documents — `required: 1` for "any one of these will do". A car worth under
   * $5,000 gets one over the build plate and the compliance plate.
   *
   * `entity_groups` is a shared table and a message thread is a group too, so
   * `group_type` is what says this is a document rule.
   */
  entity_groups?: { id?: string | number; group_type?: string | null; required?: number | null }[] | null;
};

export type DocumentTemplateResponse = {
  id: number | string;
  description: string;
  name: string;
  required_count: number;
  /** Max documents allowed for this template. If absent, no limit. */
  maximum_count?: number;
  supported_content_types: string[];
  /** Use for CMS/Contentful lookup when present */
  content_key?: string;
  /** Stable identifier for the template, e.g. `drivers_license_front`. */
  code?: string;
  /**
   * Where this document sits within its section, stamped by the BFF alongside
   * the section itself.
   *
   * Not stored on the template either — it was a seeded column until the
   * section and the position were put together in one map, on the services that
   * render a form. Within a section, not across the checklist: `group_display_order`
   * has to lead when sorting, or the sections interleave.
   */
  display_order?: number;
  /**
   * The section this document renders under, stamped by the BFF on the way out.
   *
   * Not stored on the template: `document_templates` is shared with the
   * purchase flow, which has no sections at all, so the map from document code
   * to section lives with the services that render one. This used to be
   * expanded three hops from the request (approval → template → its request
   * templates), back when a request carried one bundle per section.
   */
  group?: string | null;
  group_name?: string | null;
  group_display_order?: number | null;
};

export type CustomerDocumentResponse = {
  id: number | string;
  content_type: string;
  filename: string;
  uploaded_at?: string | null;
};

export enum DocumentApprovalStatus {
  PENDING = 'pending',
  AWAITING_SUBMISSION = 'awaiting_submission',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  SUBMITTED = 'submitted',
}

/** Statuses that require the user to upload/submit documents (show in upload form and menu notification). SUBMITTED = blocked. */
export const APPROVAL_STATUSES_REQUIRING_UPLOAD: DocumentApprovalStatus[] = [
  DocumentApprovalStatus.PENDING, // uploaded but not submitted yet => can delete/re-upload
  DocumentApprovalStatus.AWAITING_SUBMISSION,
];

export const isApprovalAwaitingUpload = (status: DocumentApprovalStatus): boolean =>
  APPROVAL_STATUSES_REQUIRING_UPLOAD.includes(status);

export const isApprovalRequiringAction = (status: string): boolean =>
  status === DocumentApprovalStatus.PENDING || status === DocumentApprovalStatus.AWAITING_SUBMISSION;

export type ListDocumentRequestsParams = {
  limit?: number;
  offset?: number;
  order_id?: string;
};

export type SubmitDocumentParams = {
  file?: File;
  existing_document_id?: string;
  content_type?: string;
};
